/**
 * Base para Web Components em Light DOM (sem Shadow DOM).
 * Uso: class MinhaSecao extends EssenceBaseComponent { init() { ... } }
 * Opcional: destroy() — limpar Swiper/timers/listeners em window ou document sem signal; deve ser idempotente.
 * Chamado antes de re-init no editor (shopify:section:load) e no disconnectedCallback (secção removida).
 * teamdropInitProductCards: inicialização global de .product-card (depois de theme.init no load).
 * Carregar no layout: <script src="{{ 'base.js' | asset_url }}" defer></script>
 */

class EssenceBaseComponent extends HTMLElement {
  /**
   * Override em subclasses para ajustar lazy init:
   *   get eagerInit() { return true; }                   // dispara init imediato (sem observer)
   *   get observerRootMargin() { return "..."; }         // padrão: dispara quando o elemento entra 15% do viewport
   *   get observerThreshold() { return 0; }              // qual fração do alvo precisa estar visível
   */
  get eagerInit() { return false; }
  get observerRootMargin() { return "0px 0px -15% 0px"; }
  get observerThreshold() { return 0; }

  connectedCallback() {
    if (this.__tdConnected) return;
    this.__tdConnected = true;

    this.__tdController = new AbortController();
    const signal = this.__tdController.signal;

    // Listeners do Shopify Editor: só registrar em design_mode (não disparam fora do editor).
    // OBS: shopify:section:load NÃO é tratado aqui de propósito. Quando a section é
    // recarregada pelo editor, o Shopify substitui toda a innerHTML, então os elementos
    // antigos disparam disconnectedCallback (AbortController limpa listeners) e os novos
    // disparam connectedCallback (init fresh). Adicionar re-init manual aqui causa
    // double-bind nos componentes recém-criados.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener(
        "shopify:block:select",
        (event) => {
          if (event.target && this.contains(event.target)) {
            this.onEditorBlockSelect?.(event);
          }
        },
        { signal }
      );

      document.addEventListener(
        "shopify:block:deselect",
        (event) => {
          if (event.target && this.contains(event.target)) {
            this.onEditorBlockDeselect?.(event);
          }
        },
        { signal }
      );

      document.addEventListener(
        "shopify:section:select",
        (event) => {
          if (event.target && event.target.contains(this)) {
            this.onEditorSectionSelect?.(event);
          }
        },
        { signal }
      );

      document.addEventListener(
        "shopify:section:deselect",
        (event) => {
          if (event.target && event.target.contains(this)) {
            this.onEditorSectionDeselect?.(event);
          }
        },
        { signal }
      );

      document.addEventListener(
        "shopify:section:unload",
        (event) => {
          if (event.target && event.target.contains(this)) {
            this.onEditorSectionUnload?.(event);
          }
        },
        { signal }
      );
    }

    var isEager = this.hasAttribute("data-eager") || this.eagerInit === true;
    if (isEager || !("IntersectionObserver" in window)) {
      this.initOnce();
    } else {
      this.__tdObserver = new IntersectionObserver(
        (entries) => {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              this.initOnce();
              if (this.__tdObserver) {
                this.__tdObserver.disconnect();
                this.__tdObserver = null;
              }
              break;
            }
          }
        },
        {
          rootMargin: this.observerRootMargin,
          threshold: this.observerThreshold,
        }
      );
      this.__tdObserver.observe(this);
    }
  }

  initOnce() {
    if (this.__tdInitialized) return;
    this.__tdInitialized = true;
    this.init();
  }

  init() {}

  disconnectedCallback() {
    this.destroy?.();
    if (this.__tdObserver) {
      this.__tdObserver.disconnect();
      this.__tdObserver = null;
    }
    if (this.__tdController) {
      this.__tdController.abort();
      this.__tdController = null;
    }
    this.__tdConnected = false;
    this.__tdInitialized = false;
  }
}

window.EssenceBaseComponent = EssenceBaseComponent;

/**
 * Trava de rolagem compartilhada: a página só volta a rolar quando TODAS as camadas
 * (gavetas, modais) que pediram a trava soltarem.
 *   theme.scrollLock.add(el)  /  theme.scrollLock.remove(el)
 */
theme.scrollLock = {
  owners: new Set(),
  add: function (owner) {
    this.owners.add(owner);
    document.body.classList.add("u-noscroll");
  },
  remove: function (owner) {
    this.owners.delete(owner);
    if (this.owners.size === 0) document.body.classList.remove("u-noscroll");
  },
};

/**
 * <side-sheet> — gaveta única do tema: laterais (menu do celular, carrinho, filtros, busca) e janelas
 * (data-side="center": pop-up, senha, guia de medidas, formas de pagamento, endereços).
 * Markup em snippets/sheet.liquid:
 *   <side-sheet id="X" class="sheet" data-side="left|right">
 *     <div class="sheet__overlay" data-sheet-close></div>
 *     <div class="sheet__panel" role="dialog" aria-modal="true" tabindex="-1">
 *       <div class="sheet__header">[voltar] título [fechar]</div>
 *       <div class="sheet__content">…</div>
 *       <div class="sheet__footer">…</div>   (opcional)
 *     </div>
 *   </side-sheet>
 *
 * Abrir: qualquer [data-sheet-open="X"] da página (um listener no document). Fechar: [data-sheet-close]
 * dentro da gaveta (inclusive em conteúdo trocado por AJAX), overlay, Esc.
 * Entrada em sequência: filhos de [data-sheet-stagger] recebem --i ao abrir (o CSS usa no atraso).
 * Foco vai para o painel ao abrir e volta para o botão que abriu ao fechar.
 *
 * API: el.open(opener?), el.close(), el.isOpen. Eventos (bubbles): "sheet:open", "sheet:close".
 */
class SideSheet extends EssenceBaseComponent {
  get eagerInit() { return true; }

  get isOpen() { return this.hasAttribute("open"); }

  init() {
    var signal = this.__tdController.signal;
    this.addEventListener("click", this.__onClick.bind(this), { signal: signal });
    document.addEventListener("keydown", this.__onKeydown.bind(this), { signal: signal });
    this.addEventListener("pointerdown", this.__onDragStart.bind(this), { signal: signal });
  }

  /* Celular (folha de baixo): arrastar a alça ou o cabeçalho para baixo; soltar depois de 30% da altura fecha. */
  __onDragStart(e) {
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    if (!e.target.closest("[data-sheet-drag], .sheet__header") || e.target.closest("button, a")) return;
    var panel = this.querySelector(".sheet__panel");
    var overlay = this.querySelector(".sheet__overlay");
    if (!panel) return;
    var startY = e.clientY;
    var height = panel.getBoundingClientRect().height;
    var dy = 0;
    var self = this;
    panel.style.transition = "none";
    var move = function (ev) {
      dy = Math.max(0, ev.clientY - startY);
      panel.style.transform = "translateY(" + dy + "px)";
      if (overlay) overlay.style.opacity = String(1 - dy / height);
    };
    var end = function () {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      panel.style.transition = "";
      if (overlay) overlay.style.opacity = "";
      if (dy > height * 0.3) self.close();
      panel.style.transform = "";
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }

  __onClick(e) {
    if (e.target.closest("[data-sheet-close]")) {
      e.preventDefault();
      this.close();
    }
  }

  __onKeydown(e) {
    if (e.key === "Escape" && this.isOpen) this.close();
  }

  open(opener) {
    if (this.isOpen) return;
    this.opener = opener || document.activeElement;
    this.__liftToBody();
    this.querySelectorAll("[data-sheet-stagger]").forEach(function (list) {
      Array.prototype.forEach.call(list.children, function (child, index) {
        child.style.setProperty("--i", index);
      });
    });
    this.setAttribute("open", "");
    theme.scrollLock.add(this);
    if (this.opener && this.opener.setAttribute) this.opener.setAttribute("aria-expanded", "true");
    var panel = this.querySelector(".sheet__panel");
    if (panel) panel.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent("sheet:open", { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute("open");
    theme.scrollLock.remove(this);
    if (this.opener && this.opener.setAttribute) {
      this.opener.setAttribute("aria-expanded", "false");
      if (document.contains(this.opener)) this.opener.focus({ preventScroll: true });
    }
    this.dispatchEvent(new CustomEvent("sheet:close", { bubbles: true }));
    this.__returnHome();
  }

  /* Toda gaveta/janela abre no fim do <body>: centralizada na tela e por cima de tudo, mesmo quando
     foi escrita dentro de um elemento animado/transformado (preço, card, visão rápida) ou de outra gaveta.
     Depois da animação de fechar volta para o lugar de origem (o conteúdo em volta pode ser re-renderizado).
     Não sobe quando está no modo coluna (filtros no computador: posição static). */
  __liftToBody() {
    if (this.__homeMarker || !this.parentElement || this.parentElement === document.body) return;
    if (getComputedStyle(this).position === "static") return;
    this.__homeMarker = document.createComment("side-sheet");
    this.parentElement.insertBefore(this.__homeMarker, this);
    document.body.appendChild(this);
    void this.offsetWidth; // registra o estado fechado antes de abrir (senão a animação de entrada some)
  }

  __returnHome() {
    var marker = this.__homeMarker;
    if (!marker) return;
    this.__homeMarker = null;
    var self = this;
    window.setTimeout(function () {
      // Reaberta antes de terminar de fechar: continua no <body> e lembra o lugar de origem
      if (self.isOpen) {
        self.__homeMarker = marker;
        return;
      }
      // Já tirada da página por quem a criou (ex.: visão rápida) ou lugar de origem re-renderizado: some
      if (!self.isConnected || !marker.isConnected) {
        marker.remove();
        self.remove();
        return;
      }
      marker.parentNode.replaceChild(self, marker);
    }, 650);
  }

  onEditorSectionUnload() {
    this.close();
  }

  destroy() {
    if (this.isOpen) theme.scrollLock.remove(this);
  }
}

if (window.customElements && !customElements.get("side-sheet")) {
  customElements.define("side-sheet", SideSheet);
}

window.SideSheet = SideSheet;

/* "Entrar" de dentro de uma gaveta (aba Conta do menu lateral): fecha a gaveta e, terminada a animação,
   aciona o login da Shopify que fica fora das gavetas (#AccountLogin, layout/theme.liquid). Assim nunca fica
   uma janela dentro da outra, e a da Shopify não some junto com a gaveta. */
document.addEventListener("click", function (e) {
  var button = e.target.closest("[data-account-login]");
  if (!button) return;
  var trigger = document.querySelector("#AccountLogin [data-account-login-trigger]");
  if (!trigger) return;
  var sheet = button.closest("side-sheet");
  if (sheet && sheet.isOpen) sheet.close();
  window.setTimeout(function () {
    trigger.click();
  }, sheet ? 400 : 0);
});

/**
 * <close-cursor> — cursor "fechar" das gavetas/janelas (layout/theme.liquid, um só na página).
 * Com mouse: sobre o fundo escuro de uma gaveta aberta (.sheet[open] > .sheet__overlay) o cursor some
 * e uma bolinha com o X cresce e segue o ponteiro com atraso suave (anda 1/4 do caminho a cada quadro);
 * apertar o botão aumenta a bolinha. O clique continua sendo do fundo (fecha pelo [data-sheet-close]).
 * Toque/caneta: não faz nada (o CSS nem mostra). Estado: classes is-active / is-pressed.
 * Leve: só escuta o mouse enquanto houver gaveta aberta (sheet:open → sheet:close da última) e só
 * mexe na posição enquanto a bolinha está visível.
 */
class CloseCursor extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    var signal = this.__tdController.signal;
    var self = this;
    this.x = this.targetX = -100;
    this.y = this.targetY = -100;

    document.addEventListener("sheet:open", function () {
      self.__watch(true);
    }, { signal: signal });
    document.addEventListener("sheet:close", function () {
      self.setActive(false);
      // Para de escutar o mouse quando não sobra nenhuma gaveta aberta
      if (!document.querySelector(".sheet[open]")) self.__watch(false);
    }, { signal: signal });
  }

  /* Liga/desliga a escuta do mouse (só com gaveta aberta) */
  __watch(on) {
    if (on === !!this.__moves) return;
    if (!on) {
      this.__moves.abort();
      this.__moves = null;
      return;
    }
    this.__moves = new AbortController();
    var signal = this.__moves.signal;
    var self = this;
    document.addEventListener("pointermove", function (e) {
      if (e.pointerType !== "mouse") return;
      var over = !!(e.target.closest && e.target.closest(".sheet[open] > .sheet__overlay"));
      if (!over && !self.active) return;
      self.targetX = e.clientX;
      self.targetY = e.clientY;
      // Ao entrar no fundo, nasce já embaixo do ponteiro (sem "voar" do último lugar)
      if (over && !self.active) {
        self.x = self.targetX;
        self.y = self.targetY;
      }
      self.setActive(over);
      if (over) self.__follow();
    }, { passive: true, signal: signal });
    document.addEventListener("pointerdown", function () {
      if (self.active) self.classList.add("is-pressed");
    }, { signal: signal });
    document.addEventListener("pointerup", function () {
      self.classList.remove("is-pressed");
    }, { signal: signal });
  }

  setActive(active) {
    if (this.active === active) return;
    this.active = active;
    this.classList.toggle("is-active", active);
    if (!active) this.classList.remove("is-pressed");
  }

  __follow() {
    if (this.__frame) return;
    var self = this;
    var step = function () {
      self.x += (self.targetX - self.x) / 4;
      self.y += (self.targetY - self.y) / 4;
      self.style.setProperty("--cursor-x", self.x + "px");
      self.style.setProperty("--cursor-y", self.y + "px");
      var still = Math.abs(self.targetX - self.x) + Math.abs(self.targetY - self.y) < 0.5;
      self.__frame = still ? null : window.requestAnimationFrame(step);
    };
    this.__frame = window.requestAnimationFrame(step);
  }

  destroy() {
    if (this.__frame) window.cancelAnimationFrame(this.__frame);
    this.__frame = null;
    if (this.__moves) this.__moves.abort();
    this.__moves = null;
  }
}

if (window.customElements && !customElements.get("close-cursor")) {
  customElements.define("close-cursor", CloseCursor);
}

/**
 * <floating-bar> — barra/botão fixo no pé da tela que aparece conforme um elemento da página. ÚNICO do tema:
 * "Filtrar e ordenar" da coleção e barra de compra do produto no celular.
 *
 *   <floating-bar class="floating-bar floating-bar--center|--full" data-watch="#Alvo" [data-show="passed"] inert>…</floating-bar>
 *
 * - data-show="visible" (padrão): aparece enquanto o alvo está na tela.
 * - data-show="passed": aparece depois que o alvo saiu por cima (rolou além dele).
 * Visível = atributo data-shown (o CSS anima); escondida fica inert (fora do teclado/leitor).
 * Sobe junto com o dock do celular (--dock-height no CSS).
 */
class FloatingBar extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    var target = this.dataset.watch ? document.querySelector(this.dataset.watch) : null;
    if (!target || !("IntersectionObserver" in window)) {
      this.setVisible(true);
      return;
    }
    var passed = this.dataset.show === "passed";
    var self = this;
    this.__observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      self.setVisible(passed ? !entry.isIntersecting && entry.boundingClientRect.top < 0 : entry.isIntersecting);
    });
    this.__observer.observe(target);
  }

  setVisible(visible) {
    this.toggleAttribute("data-shown", visible);
    this.inert = !visible;
  }

  destroy() {
    if (this.__observer) this.__observer.disconnect();
  }
}

if (window.customElements && !customElements.get("floating-bar")) {
  customElements.define("floating-bar", FloatingBar);
}

/**
 * <menu-dropdown> — suspenso pequeno do tema (ex.: "Atendimento" da barra de anúncio).
 * Mesmo comportamento do menu do cabeçalho: com mouse abre ao passar (100ms) e fecha ao sair (200ms);
 * no toque/teclado o botão abre e fecha; Esc, clique fora ou foco saindo fecham.
 *
 * Markup:
 *   <menu-dropdown class="dropdown [dropdown--end]">
 *     <button type="button" class="dropdown__toggle" aria-expanded="false" aria-haspopup="true">…</button>
 *     <div class="dropdown__panel">…</div>
 *   </menu-dropdown>
 * data-click: só abre no clique (ex.: botão flutuante de atendimento).
 * Estado: atributo data-open (o CSS mostra o painel).
 */
class MenuDropdown extends EssenceBaseComponent {
  get eagerInit() { return true; }

  get isOpen() { return this.hasAttribute("data-open"); }

  init() {
    this.toggleButton = this.querySelector(":scope > .dropdown__toggle");
    if (!this.toggleButton || !this.querySelector(":scope > .dropdown__panel")) return;
    var signal = this.__tdController.signal;
    var mouse = window.matchMedia("(hover: hover) and (pointer: fine)");
    var hover = !this.hasAttribute("data-click");
    var self = this;

    this.addEventListener("pointerenter", function (e) {
      if (hover && mouse.matches && e.pointerType === "mouse") self.__later(true);
    }, { signal: signal });
    this.addEventListener("pointerleave", function (e) {
      if (hover && mouse.matches && e.pointerType === "mouse") self.__later(false);
    }, { signal: signal });
    this.toggleButton.addEventListener("click", function () {
      // Aberto pelo mouse: o clique não fecha (o cursor ainda está em cima)
      if (self.__byHover) return;
      self.setOpen(!self.isOpen);
    }, { signal: signal });
    this.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || !self.isOpen) return;
      self.setOpen(false);
      self.toggleButton.focus();
    }, { signal: signal });
    this.addEventListener("focusout", function (e) {
      if (!self.contains(e.relatedTarget)) self.setOpen(false);
    }, { signal: signal });
    document.addEventListener("click", function (e) {
      if (self.isOpen && !self.contains(e.target)) self.setOpen(false);
    }, { signal: signal });
  }

  __later(open) {
    window.clearTimeout(this.__timer);
    var self = this;
    this.__timer = window.setTimeout(function () {
      self.setOpen(open);
      self.__byHover = open;
    }, open ? 100 : 200);
  }

  setOpen(open) {
    this.toggleAttribute("data-open", open);
    if (!open) this.__byHover = false;
    this.toggleButton.setAttribute("aria-expanded", open ? "true" : "false");
  }

  /* Editor: mostra o painel enquanto a seção ou um bloco dela está selecionado */
  onEditorSectionSelect() { if (this.toggleButton) this.setOpen(true); }
  onEditorBlockSelect() { if (this.toggleButton) this.setOpen(true); }
  onEditorSectionDeselect() { if (this.toggleButton) this.setOpen(false); }

  destroy() {
    window.clearTimeout(this.__timer);
  }
}

if (window.customElements && !customElements.get("menu-dropdown")) {
  customElements.define("menu-dropdown", MenuDropdown);
}

// Delegação global para [data-sheet-open="X"].
if (!window.__teamdropDrawerOpenBound) {
  window.__teamdropDrawerOpenBound = true;
  document.addEventListener("click", function (e) {
    var opener = e.target.closest("[data-sheet-open]");
    if (!opener) return;
    var drawer = document.getElementById(opener.getAttribute("data-sheet-open"));
    if (!drawer || typeof drawer.open !== "function") return;
    e.preventDefault();
    // Botão dentro de outra gaveta (ex.: busca no rodapé do menu): fecha a de origem antes
    var from = opener.closest("side-sheet");
    if (from && from !== drawer && !from.contains(drawer)) from.close();
    // data-sheet-tab="id do painel": abre a gaveta já nessa aba (ex.: menu lateral na aba Pesquisar)
    var tabs = opener.dataset.sheetTab && drawer.querySelector("tab-element");
    if (tabs && typeof tabs.select === "function") tabs.select(opener.dataset.sheetTab);
    drawer.open(opener);
  });
}

/**
 * <accordion-element> — sanfona acessível. Markup único em snippets/accordion.liquid.
 *
 * Markup:
 *   <accordion-element class="accordion" [open]>
 *     [<h3 class="accordion__heading">]
 *       <button type="button" class="accordion-header" aria-expanded="false">…</button>
 *     [</h3>]
 *     <div class="accordion-panel"><div class="accordion-content">Conteúdo…</div></div>
 *   </accordion-element>
 *
 * - O cabeçalho é <button>: Enter/Espaço e foco vêm do navegador.
 * - O componente mantém `aria-expanded` no botão e liga `aria-controls` ao painel (gera id se faltar).
 * - Cabeçalho/painel são os do PRÓPRIO acordeão (acordeões aninhados não se misturam).
 * - Estado inicial via atributo `open` (mesma semântica do <details>).
 *   Sem `open` → painel começa fechado (height 0; o CSS esconde o conteúdo do foco/leitor).
 * - Sem transição (prefers-reduced-motion ou CSS sem transition) o estado fecha na hora.
 *
 * API: el.open (get/set do atributo), el.openPanel(), el.close(), el.toggle().
 * Eventos: "accordion:open", "accordion:close" (bubbles). Evento "contentClose" no elemento → toggle.
 */
class AccordionElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  get open() { return this.hasAttribute("open"); }
  set open(value) {
    if (value) this.setAttribute("open", "");
    else this.removeAttribute("open");
  }

  init() {
    this.accordionPanel = this.__own(".accordion-panel");
    if (!this.accordionPanel) return;
    this.accordionHeader = this.__own(".accordion-header");

    if (!this.accordionPanel.id) {
      AccordionElement.__uid = (AccordionElement.__uid || 0) + 1;
      this.accordionPanel.id = "accordion-panel-" + AccordionElement.__uid;
    }
    if (this.accordionHeader) {
      this.accordionHeader.setAttribute("aria-controls", this.accordionPanel.id);
    }

    var signal = this.__tdController.signal;
    this.addEventListener("click", this.__onClick.bind(this), { signal: signal });
    this.addEventListener("transitionend", this.__onTransitionEnd.bind(this), { signal: signal });
    this.addEventListener("contentClose", this.__onContentClose.bind(this), { signal: signal });

    // Estado inicial
    if (this.open) {
      this.classList.add("is-open");
    } else {
      this.classList.remove("is-open");
      this.accordionPanel.style.height = "0";
    }
    this.__syncAria(this.open);
  }

  /** Primeiro elemento `selector` que pertence a ESTE acordeão (ignora acordeões aninhados). */
  __own(selector) {
    var list = this.querySelectorAll(selector);
    for (var i = 0; i < list.length; i++) {
      if (list[i].closest("accordion-element") === this) return list[i];
    }
    return null;
  }

  __syncAria(expanded) {
    if (this.accordionHeader) {
      this.accordionHeader.setAttribute("aria-expanded", expanded ? "true" : "false");
    }
  }

  /** Fecha o ciclo no transitionend; sem transição (reduced motion) na hora; e uma rede de segurança
   *  caso o transitionend não venha (altura igual, aba em segundo plano). */
  __scheduleFinish() {
    window.clearTimeout(this.__finishTimer);
    var style = window.getComputedStyle(this.accordionPanel);
    var ms = (parseFloat(style.transitionDuration) + parseFloat(style.transitionDelay)) * 1000;
    if (!(ms > 0)) { this.__finish(); return; }
    this.__finishTimer = window.setTimeout(this.__finish.bind(this), ms + 100);
  }

  __onClick(e) {
    var header = e.target.closest(".accordion-header");
    if (!header || header !== this.accordionHeader) return;
    e.preventDefault();
    this.toggle();
  }

  __onContentClose() {
    this.toggle();
  }

  toggle() {
    if (this.open && !this.classList.contains("is-collapsing")) this.close();
    else this.openPanel();
  }

  openPanel() {
    this.classList.remove("is-collapsing");
    this.accordionPanel.style.height = "0";
    this.open = true;
    this.__syncAria(true);
    this.__setPanelHeight();
    var self = this;
    window.setTimeout(function () {
      self.classList.add("is-open");
      self.__scheduleFinish();
    }, 10);
    this.dispatchEvent(new CustomEvent("accordion:open", { bubbles: true }));
  }

  close() {
    this.__setPanelHeight();
    this.classList.remove("is-open");
    this.classList.add("is-collapsing");
    this.__syncAria(false);
    var self = this;
    window.setTimeout(function () {
      self.accordionPanel.style.height = "0";
      self.__scheduleFinish();
    }, 10);
    this.dispatchEvent(new CustomEvent("accordion:close", { bubbles: true }));
  }

  __setPanelHeight() {
    this.accordionPanel.style.height = this.accordionPanel.scrollHeight + "px";
    // Força o navegador a registrar a altura em px antes da próxima mudança
    // (sem isso, auto → 0 não anima e o transitionend não dispara).
    void this.accordionPanel.offsetHeight;
  }

  __onTransitionEnd(e) {
    if (e.target !== this.accordionPanel || e.propertyName !== "height") return;
    this.__finish();
  }

  destroy() {
    window.clearTimeout(this.__finishTimer);
  }

  __finish() {
    window.clearTimeout(this.__finishTimer);
    if (this.classList.contains("is-collapsing")) {
      // Fechou: limpa is-collapsing e remove o atributo open (mantém height 0 inline).
      this.classList.remove("is-collapsing");
      this.open = false;
    } else if (this.open) {
      // Abriu: volta a height auto (acompanha mudanças de conteúdo).
      this.accordionPanel.style.removeProperty("height");
    }
  }
}

if (window.customElements && !customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}
window.AccordionElement = AccordionElement;

/**
 * <tab-element> — sistema de abas com nav + paineis.
 * Substitui theme.component.tab (era em global.js).
 *
 * Markup:
 *   <tab-element class="ab-tab">
 *     <div class="ab-tab__nav" data-tab-panel-id="painelA">Tab A</div>
 *     <div class="ab-tab__nav" data-tab-panel-id="painelB">Tab B</div>
 *     <div class="ab-tab__panel" id="painelA">...</div>
 *     <div class="ab-tab__panel" id="painelB">...</div>
 *   </tab-element>
 *
 * Variante "barra de pílulas" (snippets/tabs-pills.liquid): a nav é um <div class="tabs tabs--pills"
 * role="tablist"> com <button class="ab-tab__nav tabs__item" role="tab">. Com role="tab" o componente
 * mantém aria-selected/tabindex e aceita setas/Home/End. A barra rola na horizontal; o componente liga
 * data-overflow-start / data-overflow-end na barra quando há conteúdo escondido daquele lado (sombras no CSS)
 * e rola a pílula ativa para a vista.
 *
 * Aba ativa inicial: a nav/painel que já vierem com a classe ativa no HTML (senão, a primeira).
 * API: el.select(panelId). Evento "tab:change" (bubbles) com detail { panelId, nav, panel }.
 */
class TabElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    this.activeClass = "ab-tab__nav--active";
    this.panelActiveClass = "ab-tab__panel--active";

    var navs = this.__navs();
    var panels = this.__panels();
    if (navs.length === 0) return;

    var activeNav = navs.find(function (nav) { return nav.classList.contains("ab-tab__nav--active"); });
    if (!activeNav) {
      activeNav = navs[0];
      activeNav.classList.add(this.activeClass);
    }
    if (panels.length > 0 && !panels.some(function (p) { return p.classList.contains("ab-tab__panel--active"); })) {
      var first = this.__panelFor(activeNav) || panels[0];
      first.classList.add(this.panelActiveClass);
    }
    this.__syncAria();

    var self = this;
    var signal = this.__tdController.signal;
    navs.forEach(function (nav) {
      nav.addEventListener("click", self.__onNavClick.bind(self), { signal: signal });
      if (nav.getAttribute("role") === "tab") {
        nav.addEventListener("keydown", self.__onNavKeydown.bind(self), { signal: signal });
      }
    });

    this.querySelectorAll(".tabs--pills").forEach(function (bar) {
      self.__watchOverflow(bar, signal);
    });
  }

  /* Só as navs/painéis deste componente (abas aninhadas não se misturam) */
  __navs() {
    var self = this;
    return Array.prototype.filter.call(this.querySelectorAll(".ab-tab__nav"), function (el) {
      return el.closest("tab-element") === self;
    });
  }

  __panels() {
    var self = this;
    return Array.prototype.filter.call(this.querySelectorAll(".ab-tab__panel"), function (el) {
      return el.closest("tab-element") === self;
    });
  }

  __panelFor(nav) {
    var id = nav && nav.dataset.tabPanelId;
    if (!id) return null;
    return this.__panels().find(function (p) { return p.id === id; }) || null;
  }

  __onNavClick(e) {
    e.preventDefault();
    var nav = e.target.closest(".ab-tab__nav");
    if (!nav) return;
    this.select(nav.dataset.tabPanelId);
  }

  /* Setas / Home / End entre as abas (só na variante com role="tab") */
  __onNavKeydown(e) {
    var navs = this.__navs();
    var index = navs.indexOf(e.currentTarget);
    var next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = navs[(index + 1) % navs.length];
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = navs[(index - 1 + navs.length) % navs.length];
    else if (e.key === "Home") next = navs[0];
    else if (e.key === "End") next = navs[navs.length - 1];
    if (!next) return;
    e.preventDefault();
    this.select(next.dataset.tabPanelId);
    next.focus();
  }

  select(panelId) {
    var nav = this.__navs().find(function (n) { return n.dataset.tabPanelId === panelId; });
    if (!nav) return;
    var alreadyActive = nav.classList.contains(this.activeClass);

    this.__navs().forEach(function (n) { n.classList.remove("ab-tab__nav--active"); });
    this.__panels().forEach(function (p) { p.classList.remove("ab-tab__panel--active"); });

    nav.classList.add(this.activeClass);
    var targetPanel = this.__panelFor(nav);
    if (targetPanel) targetPanel.classList.add(this.panelActiveClass);
    this.__syncAria();
    this.__revealNav(nav);

    if (!alreadyActive) {
      this.dispatchEvent(new CustomEvent("tab:change", {
        bubbles: true,
        detail: { panelId: panelId, nav: nav, panel: targetPanel },
      }));
    }
  }

  __syncAria() {
    var self = this;
    this.__navs().forEach(function (nav) {
      if (nav.getAttribute("role") !== "tab") return;
      var active = nav.classList.contains("ab-tab__nav--active");
      nav.setAttribute("aria-selected", active ? "true" : "false");
      nav.setAttribute("tabindex", active ? "0" : "-1");
      var panel = self.__panelFor(nav);
      if (panel) panel.hidden = !active;
    });
  }

  /* Barra que rola: marca os lados com conteúdo escondido e mostra a pílula ativa */
  __watchOverflow(bar, signal) {
    var update = function () {
      var max = bar.scrollWidth - bar.clientWidth;
      var x = Math.abs(bar.scrollLeft);
      bar.toggleAttribute("data-overflow-start", x > 1);
      bar.toggleAttribute("data-overflow-end", max - x > 1);
    };
    bar.addEventListener("scroll", update, { passive: true, signal: signal });
    if ("ResizeObserver" in window) {
      this.__tdResize = this.__tdResize || new ResizeObserver(function (entries) {
        entries.forEach(function (entry) { entry.target.dispatchEvent(new Event("scroll")); });
      });
      this.__tdResize.observe(bar);
    }
    update();
    var active = bar.querySelector(".ab-tab__nav--active");
    if (active) this.__revealNav(active);
  }

  __revealNav(nav) {
    var bar = nav.closest(".tabs--pills");
    if (!bar || bar.scrollWidth <= bar.clientWidth) return;
    var left = nav.offsetLeft - (bar.clientWidth - nav.offsetWidth) / 2;
    bar.scrollTo({ left: left, behavior: "smooth" });
  }

  destroy() {
    if (this.__tdResize) {
      this.__tdResize.disconnect();
      this.__tdResize = null;
    }
  }
}

if (window.customElements && !customElements.get("tab-element")) {
  customElements.define("tab-element", TabElement);
}
window.TabElement = TabElement;

/**
 * Tooltip — sistema centralizado, sem Web Component.
 * Tooltips são marcadores em elementos pré-existentes ([data-tooltip-text]),
 * então fica como helper auto-bind por scan + delegação de hover.
 *
 * Substitui theme.component.tooltip + theme.component.renderTooltip.
 */
function teamdropInitTooltips(root) {
  if (window.innerWidth < 1024) return;
  if (!root || !root.querySelectorAll) return;
  root
    .querySelectorAll("[data-tooltip-text]:not([data-tooltip-rendered])")
    .forEach(teamdropBindTooltip);
}

function teamdropBindTooltip(toggle) {
  var TOOLTIP_ARROW_SIZE = 10;

  function createTip() {
    var tip = document.createElement("div");
    tip.classList.add("tooltip", "tooltip--" + (toggle.dataset.tooltipPosition || "bottom"));
    tip.setAttribute("role", "tooltip");
    tip.innerHTML = '<div class="tooltip-inner">' + toggle.dataset.tooltipText + "</div>";
    return tip;
  }

  function setPosition(tip) {
    var w = tip.clientWidth;
    var h = tip.clientHeight;
    var rect = toggle.getBoundingClientRect();
    var top, left;
    var position = toggle.dataset.tooltipPosition;
    switch (position) {
      case "top":
        top = rect.y;
        left = rect.x + (rect.width - w) / 2;
        tip.style.transform = "translateY(calc((100% + " + (TOOLTIP_ARROW_SIZE + 20) + "px) * -1))";
        break;
      case "left":
        top = rect.y + (rect.height - h) / 2;
        left = rect.x;
        tip.style.transform = "translateX(calc((100% + " + (TOOLTIP_ARROW_SIZE + 20) + "px) * -1))";
        break;
      case "right":
        top = rect.y + (rect.height - h) / 2;
        left = rect.x + rect.width + TOOLTIP_ARROW_SIZE;
        tip.style.transform = "translateX(20px)";
        break;
      default:
        top = rect.y + rect.height + TOOLTIP_ARROW_SIZE;
        left = rect.x + (rect.width - w) / 2;
        tip.style.transform = "translateY(20px)";
    }
    tip.style.translate = left + "px " + top + "px";
  }

  function animateIn(tip) {
    tip.style.transition = "transform 0.3s ease-in-out, opacity 0.3s ease-in-out";
    var position = toggle.dataset.tooltipPosition;
    switch (position) {
      case "top":
        tip.style.transform = "translateY(calc((100% + " + TOOLTIP_ARROW_SIZE + "px) * -1))";
        break;
      case "left":
        tip.style.transform = "translateX(calc((100% + " + TOOLTIP_ARROW_SIZE + "px) * -1))";
        break;
      case "right":
        tip.style.transform = "translateX(0%)";
        break;
      default:
        tip.style.transform = "translateY(0%)";
    }
  }

  var currentTip = null;

  function onEnter() {
    currentTip = createTip();
    document.body.append(currentTip);
    setPosition(currentTip);
    window.setTimeout(function () {
      if (!currentTip) return;
      currentTip.classList.remove("hide");
      currentTip.classList.add("show");
      animateIn(currentTip);
    }, 10);
  }

  function onLeave() {
    if (!currentTip) return;
    currentTip.classList.remove("show");
    currentTip.classList.add("hide");
    var tip = currentTip;
    currentTip = null;
    window.setTimeout(function () {
      if (tip.parentNode) tip.parentNode.removeChild(tip);
    }, 300);
  }

  toggle.addEventListener("mouseenter", onEnter);
  toggle.addEventListener("mouseleave", onLeave);
  toggle.addEventListener("click", onLeave);
  toggle.setAttribute("data-tooltip-rendered", "");
}

window.teamdropInitTooltips = teamdropInitTooltips;

/*
 * theme.utilities — helpers transversais (animação, debounce, spinner,
 * fetch helpers, storage). Antes ficava em global.js; movido
 * pra cá porque é infra usada pelos Web Components e por todos os módulos
 * carregados depois (search, collection, product, cart, wishlist, etc.).
 *
 * Load order (defer): global.js → base.js → vendor.js → swiper-slider.js
 * → ... módulos. base.js sempre roda antes de qualquer caller, e o bootstrap
 * em global.js (window.load) também executa depois.
 */
theme.utilities = {
  /**
   * Entrada de elementos, no modelo "cascata dos visíveis": os que entram na tela juntos (mesmo pai)
   * aparecem em sequência; os de fora animam quando chegam. Classes (visual no theme.css):
   * .slideUp sobe · .fadeIn só aparece · .slideLeft vem da esquerda · .slideRight vem da direita ·
   * .blurIn sai do desfoque · .zoomIn cresce com um pulo.
   * Distância/duração/atraso: variáveis --reveal-* no theme.css (menores no celular/toque).
   * Ao terminar, as classes saem (o hover do card volta a controlar o transform).
   * Repetir (igual à linha do tempo): dentro de [data-reveal-repeat] as classes ficam e o item entra quando o topo
   * passa de 80% da altura da tela; se a pessoa sobe e ele volta para baixo desse ponto, a animação se desfaz
   * (na frente dela) e roda de novo ao descer.
   */
  revealClasses: ["slideUp", "fadeIn", "slideLeft", "slideRight", "blurIn", "zoomIn"],
  revealStep: function () {
    return window.matchMedia("(max-width: 767px), (pointer: coarse)").matches ? 50 : 100;
  },
  /* Grupo da cascata: a lista que contém o item. Sobe pelos invólucros de um filho só
     (ex.: card dentro de .swiper-slide dentro de .swiper-wrapper). */
  revealGroup: function (el) {
    var node = el.parentNode;
    for (var i = 0; i < 3 && node && node.parentNode && node.children.length === 1; i++) node = node.parentNode;
    return node;
  },
  revealEntries: function (entries, observer) {
    var step = theme.utilities.revealStep();
    var perParent = new Map();
    entries.forEach(function (entry) {
      var el = entry.target;
      var repeat = observer === theme.utilities.repeatObserver;
      if (!entry.isIntersecting) {
        if (repeat && entry.boundingClientRect.top > 0) {
          el.classList.remove("animated");
          el.style.removeProperty("--reveal-delay");
        }
        return;
      }
      if (el.classList.contains("animated")) return;
      var group = theme.utilities.revealGroup(el);
      var index = perParent.get(group) || 0;
      perParent.set(group, index + 1);
      el.style.setProperty("--reveal-delay", index * step + "ms");
      el.classList.add("animated");
      if (repeat) return;
      observer.unobserve(el);
      el.addEventListener(
        "transitionend",
        function done(event) {
          if (event.target !== el || event.propertyName !== "opacity") return;
          el.removeEventListener("transitionend", done);
          el.classList.remove.apply(el.classList, theme.utilities.revealClasses.concat("animated"));
          el.style.removeProperty("--reveal-delay");
        }
      );
    });
  },
  animationObserver: new IntersectionObserver(
    function (entries, observer) { theme.utilities.revealEntries(entries, observer); },
    { rootMargin: "0px", threshold: 0 }
  ),
  repeatObserver: new IntersectionObserver(
    function (entries, observer) { theme.utilities.revealEntries(entries, observer); },
    { rootMargin: "0px 0px -20% 0px", threshold: 0 }
  ),
  applyAnimation: function () {
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var self = this;
    var classes = this.revealClasses;
    var selector = classes.map(function (name) { return "." + name + ":not(.animated)"; }).join(", ");
    document.querySelectorAll(selector).forEach(function (el) {
      if (reduce || !("IntersectionObserver" in window)) {
        el.classList.remove.apply(el.classList, classes);
        return;
      }
      (el.closest("[data-reveal-repeat]") ? self.repeatObserver : self.animationObserver).observe(el);
    });
  },
  debounce: function (e, t = 300) {
    let i;
    return (...n) => {
      clearTimeout(i), (i = setTimeout(() => e.apply(this, n), t));
    };
  },
  getSectionUrl: function (e) {
    var t = document.getElementById("product-grid").dataset.id;
    return window.location.pathname + `?section_id=${t}&` + e;
  },
  fetchConfig: function (e = "json") {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/" + e,
      },
    };
  },
  getListFromStorage: function (e) {
    return (e = window.localStorage.getItem(e)) && 0 < e.length
      ? JSON.parse(e)
      : [];
  },
  /* Dinheiro — ÚNICO formatador do JS (centavos → "R$ 10,00" na moeda e no idioma da loja) */
  formatMoney: function (cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || "BRL";
    return new Intl.NumberFormat(document.documentElement.lang || "pt-BR", { style: "currency", currency: currency }).format(cents / 100);
  },
  toggleSpinner: function (el, on) {
    if (!el) return;
    el.classList.toggle("with-loader--show", !!on);
    var spinner = el.querySelector(".spinner");
    if (spinner) spinner.classList.toggle("animating", !!on);
  },
  fetchHtmlFragment: function (url, selector) {
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error("Fetch failed: " + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        return selector ? doc.querySelector(selector) : doc;
      });
  },
};

// Infra de notificacao global reutilizavel em todo o tema.
theme.notification = {};
theme.notification.component = {
  duration: 5000,
  type: null,
  title: null,
  text: null,
  show: function () {
    if (!this.type) return;

    this.item = document.createElement("div");
    this.item.classList.add(
      "notification",
      "notification--" + this.type,
      "is-sliding",
      "translate-x-hidden"
    );

    var content = document.createElement("div");
    content.classList.add("notification-content");

    if (this.title) {
      var titleEl = document.createElement("div");
      titleEl.classList.add("h6");
      titleEl.innerHTML = this.title;
      content.appendChild(titleEl);
    }

    if (this.text) {
      var textEl = document.createElement("div");
      textEl.innerHTML = this.text;
      content.appendChild(textEl);
    }

    var buttonContainer = document.createElement("div");
    buttonContainer.classList.add("notification-btn");
    var button = document.createElement("button");
    button.innerHTML = window.icons.cross;
    button.addEventListener("click", this.handleBtnClose.bind(this));
    buttonContainer.appendChild(button);
    content.appendChild(buttonContainer);
    this.item.appendChild(content);

    var container = document.querySelector(".notification-container");
    if (!container) {
      container = document.createElement("div");
      container.classList.add("notification-container");
      document.querySelector("body").append(container);
    }
    container.append(this.item);

    setTimeout(() => {
      this.item.classList.remove("translate-x-hidden");
      this.item.classList.add("translate-0");
    }, 300);

    this.hideByDurationId = setTimeout(() => {
      this.hide();
    }, this.duration);
  },
  handleBtnClose: function (event) {
    event.preventDefault();
    clearTimeout(this.hideByDurationId);
    this.hide();
  },
  hide: function () {
    this.item.classList.add("translate-x-hidden");
    this.item.classList.remove("translate-0");
    setTimeout(() => {
      this.item.remove();
    }, 1000);
  },
};

theme.notification.error = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "error";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.notification.success = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "success";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.notification.info = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "info";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.notification.warning = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "warning";
  notification.title = title;
  notification.text = text;
  notification.show();
};

/**
 * Inicializa cartões de produto (swatches, tooltips) em qualquer raiz do DOM.
 * Faz setupCardRoot + renderTooltip num só passo. Idempotente via [card-rendered].
 * Depende de ProductCardElement.setupCardRoot (assets/product.js).
 */
function teamdropInitProductCards(root) {
  if (!root || typeof ProductCardElement === "undefined" || !ProductCardElement.setupCardRoot) return;
  root.querySelectorAll(".product-card:not([card-rendered])").forEach(function (el) {
    ProductCardElement.setupCardRoot(el);
  });
  teamdropInitTooltips(root);
}

/**
 * <link-copier> — botão que copia link para o clipboard.
 *
 * Markup:
 *   <link-copier class="link-copier no-js-hidden">
 *     <input type="hidden" value="{{ url }}">
 *     <button class="link-copier__btn">Copiar</button>
 *     <span class="link-copier__success u-none">Copiado!</span>
 *   </link-copier>
 */
class LinkCopierElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    var btn = this.querySelector(".link-copier__btn");
    if (!btn) return;
    btn.addEventListener("click", this.__onClick.bind(this), {
      signal: this.__tdController.signal,
    });
  }

  __onClick() {
    var input = this.querySelector("input");
    var url = (input && input.value) || document.location.href;
    var success = this.querySelector(".link-copier__success");
    var self = this;
    navigator.clipboard.writeText(url).then(function () {
      if (!success) return;
      success.classList.remove("u-none");
      success.textContent = window.accessibilityStrings && window.accessibilityStrings.shareSuccess;
      window.setTimeout(function () {
        success.classList.add("u-none");
      }, 5000);
    });
  }
}

if (window.customElements && !customElements.get("link-copier")) {
  customElements.define("link-copier", LinkCopierElement);
}

/**
 * <localization-form> — submita o form pai quando um <select> filho muda.
 * Substitui theme.component.localization (era em global.js).
 *
 * Markup:
 *   <localization-form>
 *     <select class="localization-selector link" name="locale_code">...</select>
 *   </localization-form>
 */
class LocalizationFormElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    var select = this.querySelector("select");
    if (!select) return;
    select.addEventListener("change", this.__onChange.bind(this), {
      signal: this.__tdController.signal,
    });
  }

  __onChange(e) {
    var form = e.currentTarget.closest("form");
    if (form) form.submit();
  }
}

if (window.customElements && !customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationFormElement);
}

// Auto-init: script defer roda após DOM parseado. Sem window.load.
// <product-tile> é Web Component lazy — não precisa de init global aqui.
function teamdropBootstrapBase() {
  teamdropInitTooltips(document);
}

if (document.readyState === "complete") {
  teamdropBootstrapBase();
} else {
  document.addEventListener("DOMContentLoaded", teamdropBootstrapBase);
}

document.addEventListener("shopify:section:load", function (event) {
  if (!event.target) return;
  teamdropInitProductCards(event.target);
  teamdropInitTooltips(event.target);
});

window.teamdropInitProductCards = teamdropInitProductCards;
